/**
 * Convention d'ancre de message — `message_<messageId>` (contrat C1, projet seo-forums-reworld_P2).
 *
 * L'ancre elle-même est posée côté client par `ThreadMessage/ThreadFlattenMessage.tsx`
 * (via `AvoidHeaderAnchor` → `<div id="message_<id>">`, id encore construit en dur là-bas : hors
 * write-set de B1_02). Ce module tient les deux bouts du LIEN : comment on écrit l'ancre dans un
 * href, et comment on la relit à l'arrivée.
 *
 * B1_02 — le lien « retour au message » du logo ciblait cette ancre par un paramètre
 * `?messageId=<id>`. `makeCleanRoute2` sérialise en query string tout paramètre non consommé par
 * le pattern de route : le lien produisait `/t/<slug>?messageId=<id>`, puis `/?messageId=<id>` une
 * fois la page hydratée — deux familles d'URL que Googlebot crawlait sans jamais les indexer
 * (22,5 % du crawl sur Beauté Test). Le fragment `#message_<id>` désigne la même ancre sans
 * fabriquer d'URL distincte pour un moteur.
 *
 * L'ancienne forme `?messageId=` reste LUE : des liens de cette forme sont déjà indexés, en favoris
 * et dans des historiques de navigation.
 */

/**
 * Forme permissive sur l'id — les `_id` Mongo sont hexadécimaux, mais l'ancien paramètre n'a jamais
 * été validé — tout en fermant le seul vecteur qui casse : `scrollToAnchor` injecte le nom dans un
 * sélecteur CSS `#<nom>`, et une parenthèse ou un guillemet y lève une `DOMException`. Comme il n'y a
 * aucun `ErrorBoundary` dans graphdebate, ce throw remonte jusqu'à la racine React : page blanche sur
 * une URL forgée. Ce module étant le point de passage unique, il valide les DEUX provenances.
 */
const MESSAGE_ID_RE = /^[\w-]+$/;
const MESSAGE_HASH_RE = /^#?message_([\w-]+)$/;

export const mkMessageAnchor = (messageId: string) => `message_${messageId}`;

/**
 * Identifiant du message à rejoindre, qu'il vienne du fragment (forme courante) ou de l'ancien
 * paramètre de query (liens déjà publiés). Le paramètre l'emporte : il est plus explicite, et une
 * page peut garder un fragment résiduel d'une navigation précédente.
 *
 * @param query `router.query` de la page
 * @param hash `window.location.hash` (chaîne vide acceptée)
 */
export const readMessageAnchorId = (
  query: { messageId?: string | string[] },
  hash: string,
): string | undefined => {
  const { messageId } = query;
  if (typeof messageId === 'string' && MESSAGE_ID_RE.test(messageId)) return messageId;

  return MESSAGE_HASH_RE.exec(hash)?.[1];
};

/**
 * Fenêtre d'attente de l'ancre. Large : sur mobile bridé, les réponses mettent parfois plusieurs
 * secondes à s'afficher. Coût nul — un `getElementById` toutes les 100 ms, arrêté dès qu'on trouve.
 */
const ANCHOR_WAIT_MS = 10_000;
const ANCHOR_POLL_MS = 100;

/**
 * Guette l'ancre `message_<id>` dans le DOM, puis appelle `onFound` UNE fois.
 *
 * Pourquoi guetter plutôt que chercher une fois : l'ancre est posée par `ThreadFlattenMessage`
 * quand la réponse visée est rendue — donc après le montage de la liste, et plus tard encore pour
 * une réponse imbriquée. L'implémentation d'avant B1_02 tentait le défilement à l'arrivée puis une
 * seule fois 100 ms après (`scrollToAnchor`), toujours trop tôt. Mesuré en prod le 2026-09-16 sur
 * le parcours mobile réel (page `/d/` → flèche retour) : `scrollY` restait à 0 pendant 6 s alors
 * que l'ancre était à 1266 px — la fonction livrée en 2024 (2f796d0e) ne se déclenchait jamais.
 *
 * Si l'ancre n'apparaît pas (lien périmé, message supprimé), on abandonne au bout de la fenêtre
 * sans rien faire.
 *
 * @returns une fonction d'annulation, à rendre depuis un `useEffect`.
 */
export const waitForMessageAnchor = (anchorName: string, onFound: (anchorName: string) => void) => {
  const deadline = Date.now() + ANCHOR_WAIT_MS;
  const timer = setInterval(() => {
    if (!document.getElementById(anchorName)) {
      if (Date.now() > deadline) clearInterval(timer);
      return;
    }
    clearInterval(timer);
    onFound(anchorName);
  }, ANCHOR_POLL_MS);

  return () => clearInterval(timer);
};
